import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { PanelRight, Table2 } from 'lucide-react'
import { loadManifest, loadProject } from './data'
import { exportBusinessWorkbook } from './exportWorkbook'
import { SourcePanel } from './SourcePanel'
import { UniverHost } from './UniverHost'
import { buildAuditWorkbook, candidateValuesFor, filterRecords, getBusinessRecords, type ManualEdits, type RecordFilters } from './workbookData'
import type { FrontendManifest, ParameterRecord, ProjectData, ProjectManifest, TraceSelection } from './types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

const emptyFilters: RecordFilters = { text: '', packageName: '', site: '', instanceId: '', status: '' }
export default function App() {
  const [manifest, setManifest] = useState<FrontendManifest | null>(null)
  const [project, setProject] = useState<ProjectData | null>(null)
  const [projectKey, setProjectKey] = useState(readHash().project || '')
  const [selection, setSelection] = useState<TraceSelection | null>(null)
  const [activeSheet, setActiveSheet] = useState('变压器1')
  const [filters, setFilters] = useState<RecordFilters>(emptyFilters)
  const [mobileView, setMobileView] = useState<'table' | 'trace'>('table')
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia('(max-width: 1119px)').matches)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [splitPercent, setSplitPercent] = useState(58)
  const [isResizing, setIsResizing] = useState(false)
  const [manualEdits, setManualEdits] = useState<ManualEdits>({})
  const workspaceRef = useRef<HTMLElement | null>(null)
  const resizeOrigin = useRef({ left: 0, width: 1 })
  const loadedManualStorageKey = useRef('')

  const manualStorageKey = projectKey ? `transformer-poc:manual-edits:${projectKey}` : ''

  useEffect(() => {
    if (!manualStorageKey) return
    loadedManualStorageKey.current = ''
    try {
      const stored = window.localStorage.getItem(manualStorageKey)
      const parsed = stored ? JSON.parse(stored) : {}
      setManualEdits(parsed && typeof parsed === 'object' ? parsed : {})
    } catch {
      setManualEdits({})
    }
    loadedManualStorageKey.current = manualStorageKey
  }, [manualStorageKey])

  useEffect(() => {
    if (!manualStorageKey || loadedManualStorageKey.current !== manualStorageKey) return
    try {
      window.localStorage.setItem(manualStorageKey, JSON.stringify(manualEdits))
    } catch {
      // Local storage is an enhancement; editing remains available for this session.
    }
  }, [manualEdits, manualStorageKey])

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1119px)')
    const onChange = () => setIsNarrow(media.matches)
    onChange()
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [])

  useEffect(() => {
    loadManifest().then((loaded) => {
      setManifest(loaded)
      const initial = loaded.projects.find((item) => item.project_key === projectKey) || loaded.projects[0]
      if (initial) setProjectKey(initial.project_key)
    }).catch((reason) => setError(reason instanceof Error ? reason.message : String(reason)))
  }, [])

  useEffect(() => {
    if (!manifest || !projectKey) return
    const selected = manifest.projects.find((item) => item.project_key === projectKey)
    if (!selected) return
    let alive = true
    setLoading(true)
    setError(null)
    setProject(null)
    setSelection(null)
    setFilters(emptyFilters)
    loadProject(selected).then((loaded) => {
      if (!alive) return
      setProject(loaded)
      setActiveSheet(firstUsableSheet(loaded))
      const hash = readHash()
      if (hash.record) {
        const record = loaded.provenance.parameter_records.find((item) => item.record_id === hash.record)
        if (record && hasVerifiableEvidence(loaded, record)) {
          const initialEvidence = hash.evidence || firstRecordEvidence(loaded, record)
          setSelection({
            projectId: loaded.provenance.project.project_id,
            recordId: record.record_id,
            evidenceId: initialEvidence,
          })
          setActiveSheet(sheetForRecord(loaded, record.record_id))
          if (window.matchMedia('(max-width: 1119px)').matches) setMobileView('trace')
        } else if (record) {
          updateHash(selected.project_key, undefined, undefined)
        }
      }
      setLoading(false)
    }).catch((reason) => {
      if (!alive) return
      setError(reason instanceof Error ? reason.message : String(reason))
      setLoading(false)
    })
    return () => { alive = false }
  }, [manifest, projectKey])

  useEffect(() => {
    if (!isResizing) return
    const onPointerMove = (event: PointerEvent) => {
      const { left, width } = resizeOrigin.current
      const next = ((event.clientX - left) / width) * 100
      setSplitPercent(Math.min(68, Math.max(40, next)))
    }
    const stopResizing = () => setIsResizing(false)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', stopResizing, { once: true })
    window.addEventListener('pointercancel', stopResizing, { once: true })
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', stopResizing)
      window.removeEventListener('pointercancel', stopResizing)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  const records = project ? getBusinessRecords(project.provenance, project.checklistTemplate) : []
  const filteredRecords = useMemo(() => filterRecords(records, filters), [records, filters])
  const selectableRecordIds = useMemo(
    () => new Set(records.filter((record) => project && hasVerifiableEvidence(project, record)).map((record) => record.record_id)),
    [project, records],
  )
  const workbook = useMemo(() => project
    ? buildAuditWorkbook(project.provenance, filteredRecords, manualEdits, project.checklistTemplate, records)
    : null, [project, filteredRecords, manualEdits, records])

  useEffect(() => {
    if (selection && (!filteredRecords.some((record) => record.record_id === selection.recordId) || !selectableRecordIds.has(selection.recordId))) {
      setSelection(null)
      updateHash(projectKey, undefined, undefined)
    }
  }, [filteredRecords, selectableRecordIds, selection, projectKey])

  function selectRecord(recordId: string) {
    if (!project) return
    const record = project.provenance.parameter_records.find((item) => item.record_id === recordId)
    if (!record || !hasVerifiableEvidence(project, record)) return
    const evidenceId = firstRecordEvidence(project, record)
    const next: TraceSelection = {
      projectId: project.provenance.project.project_id,
      recordId,
      evidenceId,
      sourceFileId: evidenceId ? project.viewerIndex.evidence[evidenceId]?.source_file_id : undefined,
    }
    setSelection(next)
    setActiveSheet(workbook?.sheetByRecordId[recordId] || sheetForRecord(project, recordId))
    updateHash(projectKey, recordId, next.evidenceId)
    if (isNarrow) setMobileView('trace')
  }

  function selectEvidence(evidenceId: string) {
    if (!selection || !project) return
    const evidence = project.viewerIndex.evidence[evidenceId] || project.provenance.evidence.find((item) => item.evidence_id === evidenceId)
    const next = { ...selection, evidenceId, sourceFileId: evidence?.source_file_id }
    setSelection(next)
    updateHash(projectKey, next.recordId, evidenceId)
  }

  function updateManualEdit(edit: { key: string; value: string }) {
    setManualEdits((current) => {
      if (current[edit.key] === edit.value) return current
      const next = { ...current }
      if (edit.value.trim()) next[edit.key] = edit.value
      else delete next[edit.key]
      return next
    })
  }

  function exportWorkbook(scope: 'full' | 'filtered' | 'sheet') {
    if (!project) return
    try {
      exportBusinessWorkbook(project, scope === 'full' ? records : filteredRecords, manualEdits, scope, activeSheet)
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : String(exportError))
    }
  }

  function changeProject(next: ProjectManifest) {
    setProjectKey(next.project_key)
    updateHash(next.project_key, undefined, undefined)
  }

  function beginResize(event: React.PointerEvent<HTMLButtonElement>) {
    if (isNarrow) return
    const rect = workspaceRef.current?.getBoundingClientRect()
    if (!rect) return
    resizeOrigin.current = { left: rect.left, width: rect.width }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    setIsResizing(true)
  }

  function nudgeSplit(delta: number) {
    if (isNarrow) return
    setSplitPercent((value) => Math.min(68, Math.max(40, value + delta)))
  }

  if (error && !manifest) return <div className="fatal-state"><strong>前端数据加载失败</strong><p>{error}</p></div>

  return (
    <div className="app-shell">
      {error ? <Alert variant="destructive" className="inline-error"><AlertTitle>数据加载提示</AlertTitle><AlertDescription>{error}</AlertDescription></Alert> : null}

      {loading || !project || !workbook || !manifest ? <LoadingState /> : (
        <>
          <div className="mobile-view-switcher" role="tablist" aria-label="工作区视图">
            <Button variant={mobileView === 'table' ? 'secondary' : 'ghost'} size="sm" onClick={() => setMobileView('table')}>
              <Table2 /> 表格
            </Button>
            <Button variant={mobileView === 'trace' ? 'secondary' : 'ghost'} size="sm" onClick={() => setMobileView('trace')}>
              <PanelRight /> 溯源
            </Button>
          </div>
          <main
            ref={workspaceRef}
            className={cn('workspace-grid', isNarrow && 'is-narrow', isResizing && 'is-resizing', mobileView === 'trace' && 'show-trace')}
            style={!isNarrow ? { '--split-left': `${splitPercent}%` } as CSSProperties : undefined}
          >
            <Card className="audit-panel" aria-label="审计结果">
              <CardContent className="audit-panel-content">
                <FilterBar
                  project={project}
                  manifest={manifest}
                  projectKey={projectKey}
                  filters={filters}
                  onChange={setFilters}
                  activeSheet={activeSheet}
                  onExportCurrent={() => exportWorkbook('sheet')}
                  onExportAll={() => exportWorkbook('full')}
                  onProjectChange={(value) => {
                    const next = manifest.projects.find((item) => item.project_key === value)
                    if (next) changeProject(next)
                  }}
                />
                <UniverHost
                  workbookKey={`${projectKey}-${filters.text}-${filters.packageName}-${filters.site}-${filters.instanceId}-${filters.status}`}
                  workbookData={workbook.data}
                  rowMaps={workbook.rowMaps}
                  editableCells={workbook.editableCells}
                  activeSheet={activeSheet}
                  activeRecordId={selection?.recordId}
                  selectableRecordIds={selectableRecordIds}
                  onRecordSelect={selectRecord}
                  onSheetChange={setActiveSheet}
                  onManualCellChange={updateManualEdit}
                  className="audit-workbook"
                />
              </CardContent>
            </Card>
            <button
              type="button"
              className="split-divider"
              role="separator"
              aria-orientation="vertical"
              aria-label="调整表格与溯源宽度"
              aria-valuemin={40}
              aria-valuemax={68}
              aria-valuenow={Math.round(splitPercent)}
              onPointerDown={beginResize}
              onDoubleClick={() => setSplitPercent(58)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') { event.preventDefault(); nudgeSplit(-2) }
                if (event.key === 'ArrowRight') { event.preventDefault(); nudgeSplit(2) }
                if (event.key === 'Home') { event.preventDefault(); setSplitPercent(40) }
                if (event.key === 'End') { event.preventDefault(); setSplitPercent(68) }
              }}
            >
              <span className="split-divider-grip" aria-hidden="true" />
            </button>
            <SourcePanel project={project} selection={selection} onEvidenceChange={selectEvidence} />
          </main>
        </>
      )}
    </div>
  )
}

function FilterBar({ project, manifest, projectKey, filters, onChange, onProjectChange, activeSheet, onExportCurrent, onExportAll }: { project: ProjectData; manifest: FrontendManifest; projectKey: string; filters: RecordFilters; onChange: (next: RecordFilters) => void; onProjectChange: (value: string) => void; activeSheet: string; onExportCurrent: () => void; onExportAll: () => void }) {
  const records = project.provenance.parameter_records
  const packages = unique(records.map((item) => item.package).filter(Boolean) as string[])
  const sites = unique(records.map((item) => item.site).filter(Boolean) as string[])
  const instances = unique(records.map((item) => item.instance_id).filter(Boolean) as string[])
  const statuses = unique(records.map((item) => item.status))
  const set = (key: keyof RecordFilters, value: string) => onChange({ ...filters, [key]: value })

  return <div className="filter-bar">
    <Select value={projectKey} onValueChange={onProjectChange}>
      <SelectTrigger className="project-select filter-project-select" aria-label="切换项目">
        <SelectValue placeholder="选择项目" />
      </SelectTrigger>
      <SelectContent>
        {manifest.projects.map((item) => <SelectItem value={item.project_key} key={item.project_key}>{item.name}</SelectItem>)}
      </SelectContent>
    </Select>
    <FilterSelect value={filters.packageName} placeholder="全部标书 / 子标段" options={packages} onChange={(value) => set('packageName', value)} />
    <FilterSelect value={filters.site} placeholder="全部站点" options={sites} onChange={(value) => set('site', value)} />
    <FilterSelect value={filters.status} placeholder="全部状态" options={statuses} onChange={(value) => set('status', value)} />
    <FilterSelect value={filters.instanceId} placeholder="全部设备实例" options={instances} onChange={(value) => set('instanceId', value)} />
    <div className="filter-export-actions">
      <Select value="" onValueChange={(value) => {
        if (value === 'current') onExportCurrent()
        if (value === 'all') onExportAll()
      }}>
        <SelectTrigger size="sm" className="export-select" aria-label={`导出 Excel：${activeSheet}`} title={`导出 Excel：${activeSheet}`}>
          <SelectValue placeholder="导出 Excel" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="current">导出当前 Sheet</SelectItem>
          <SelectItem value="all">导出全部 Sheet</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
}

function FilterSelect({ value, placeholder, options, onChange, full = false }: { value: string; placeholder: string; options: string[]; onChange: (value: string) => void; full?: boolean }) {
  return <Select value={value || '__all__'} onValueChange={(next) => onChange(next === '__all__' ? '' : next)}>
    <SelectTrigger size="sm" className={cn('filter-select', full && 'w-full')} aria-label={placeholder}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">{placeholder}</SelectItem>
      {options.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
    </SelectContent>
  </Select>
}

function LoadingState() {
  return <div className="loading-state" aria-hidden="true" />
}

function unique(values: string[]) { return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'zh-CN')) }

function firstUsableSheet(project: ProjectData) {
  const snapshotSheets = (project.workbookSnapshot as { deviceSheets?: Array<{ sheetName?: string }> }).deviceSheets
  return snapshotSheets?.[0]?.sheetName || '变压器1'
}

function sheetForRecord(project: ProjectData, recordId: string) {
  const rowMaps = (project.workbookSnapshot as { rowMaps?: Record<string, Record<string, string>> }).rowMaps || {}
  const target = Object.entries(rowMaps).find(([, map]) => Object.values(map).includes(recordId))
  return target?.[0] || firstUsableSheet(project)
}

function hasVerifiableEvidence(project: ProjectData, record: ParameterRecord) {
  const evidenceIds = [...new Set([
    ...(record.evidence_ids || []),
    ...candidateValuesFor(record, project.provenance).flatMap((candidate) => candidate.evidence_ids || []),
  ])]
  if (!evidenceIds.length) return false
  return evidenceIds.some((evidenceId) => Boolean(
    project.viewerIndex.evidence[evidenceId]
      || project.provenance.evidence.some((evidence) => evidence.evidence_id === evidenceId),
  ))
}

function firstRecordEvidence(project: ProjectData, record: ParameterRecord): string | undefined {
  const decision = record.ai_decision_id ? project.provenance.ai_adjudications?.find((item) => item.ai_decision_id === record.ai_decision_id) : undefined
  const candidates = candidateValuesFor(record, project.provenance)
  const selected = decision?.selected_candidate_id ? candidates.find((candidate) => candidate.candidate_id === decision.selected_candidate_id) : undefined
  return selected?.evidence_ids?.[0] || record.evidence_ids?.[0] || candidates.flatMap((candidate) => candidate.evidence_ids || [])[0]
}

function readHash() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return { project: params.get('project') || '', record: params.get('record') || '', evidence: params.get('evidence') || '' }
}

function updateHash(project: string, record?: string, evidence?: string) {
  const params = new URLSearchParams()
  if (project) params.set('project', project)
  if (record) params.set('record', record)
  if (evidence) params.set('evidence', evidence)
  window.history.replaceState(null, '', params.toString() ? `#${params.toString()}` : window.location.pathname)
}
